import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Users,
  AlertOctagon,
  CreditCard,
  Mail,
  MessageSquareQuote,
  Brain,
  Settings as SettingsIcon,
  Shield,
  ChevronRight,
  Menu,
  ArrowLeft,
  LogOut,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type AdminSectionId =
  | "users"
  | "errors"
  | "billing"
  | "revenue"
  | "email"
  | "reviews"
  | "ai"
  | "system";

export interface AdminSectionMeta {
  id: AdminSectionId;
  label: string;
  description: string;
  icon: typeof Users;
  group: "people" | "ops" | "platform";
}

export const ADMIN_SECTIONS: AdminSectionMeta[] = [
  { id: "users", label: "Users", description: "Search, inspect and manage every user account", icon: Users, group: "people" },
  { id: "reviews", label: "Reviews", description: "Moderate user reviews and curate testimonials", icon: MessageSquareQuote, group: "people" },
  { id: "revenue", label: "Revenue", description: "MRR, ARR and revenue analytics", icon: TrendingUp, group: "ops" },
  { id: "errors", label: "Errors", description: "Runtime errors, refunds and rate limit monitoring", icon: AlertOctagon, group: "ops" },
  { id: "email", label: "Email", description: "Delivery log, bounces and test sends", icon: Mail, group: "ops" },
  { id: "billing", label: "Billing", description: "Pricing, checkout, transactions and Stripe sync", icon: CreditCard, group: "ops" },
  { id: "ai", label: "AI Prompts", description: "Extraction logs, prompt versions and testing", icon: Brain, group: "platform" },
  { id: "system", label: "System", description: "Security gate, test clock and danger zone", icon: SettingsIcon, group: "platform" },
];

const GROUP_LABELS: Record<AdminSectionMeta["group"], string> = {
  people: "People",
  ops: "Operations",
  platform: "Platform",
};

interface AdminLayoutProps {
  active: AdminSectionId;
  onChange: (id: AdminSectionId) => void;
  children: ReactNode;
}

export function AdminLayout({ active, onChange, children }: AdminLayoutProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeMeta = ADMIN_SECTIONS.find((s) => s.id === active) ?? ADMIN_SECTIONS[0];

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({ title: "Signed out", description: "You have been logged out" });
      navigate("/auth", { replace: true });
    } catch {
      toast({ title: "Error signing out", description: "Please try again", variant: "destructive" });
    }
  };

  const handleSelect = (id: AdminSectionId) => {
    onChange(id);
    setMobileOpen(false);
  };

  const grouped = useMemo(
    () =>
      ADMIN_SECTIONS.reduce<Record<string, AdminSectionMeta[]>>((acc, s) => {
        (acc[s.group] = acc[s.group] || []).push(s);
        return acc;
      }, {}),
    []
  );

  const initials = (user?.email ?? "??")
    .split("@")[0]
    .split(/[._-]/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const SidebarContent = (
    <div className="flex flex-col h-full bg-card/40">
      {/* Admin header */}
      <div className="p-5 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center shadow-fluent-2">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight truncate">Admin Dashboard</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {user?.email ?? "Not signed in"}
            </p>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
          <Shield className="h-3 w-3" />
          Administrator · {initials || "??"}
        </div>
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-6">
          {(Object.keys(grouped) as Array<keyof typeof GROUP_LABELS>).map((group) => (
            <div key={group}>
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/60">
                {GROUP_LABELS[group]}
              </p>
              <ul className="space-y-1">
                {grouped[group].map((s) => {
                  const isActive = s.id === active;
                  const Icon = s.icon;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(s.id)}
                        className={cn(
                          "group relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 text-left",
                          isActive
                            ? "bg-primary/10 text-primary font-medium shadow-sm"
                            : "text-foreground/80 hover:bg-muted/60 hover:text-foreground"
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 bg-primary rounded-r-full" />
                        )}
                        <Icon
                          className={cn(
                            "h-4 w-4 shrink-0 transition-colors",
                            isActive
                              ? "text-primary"
                              : "text-muted-foreground group-hover:text-foreground"
                          )}
                        />
                        <span className="flex-1 truncate">{s.label}</span>
                        {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer actions */}
      <div className="border-t border-border/60 p-3 space-y-1">
        <Button asChild variant="ghost" size="sm" className="w-full justify-start gap-2 h-9">
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            Back to Settings
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          className="w-full justify-start gap-2 h-9 text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </div>
  );

  const ActiveIcon = activeMeta.icon;

  return (
    <div className="flex flex-col lg:flex-row w-full min-w-0">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[260px] shrink-0 border-r border-border/60 sticky top-0 h-screen">
        {SidebarContent}
      </aside>

      {/* Mobile section switcher bar */}
      <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-border/60 sticky top-0 bg-background/85 backdrop-blur z-30 w-full min-w-0">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            {SidebarContent}
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ActiveIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight truncate">{activeMeta.label}</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {GROUP_LABELS[activeMeta.group]}
            </p>
          </div>
        </div>
      </div>

      {/* Content area */}
      <main className="flex-1 min-w-0 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 min-w-0">
          {/* Section header (desktop) */}
          <header className="mb-7 hidden lg:block">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <span>Admin</span>
              <ChevronRight className="h-3 w-3" />
              <span>{GROUP_LABELS[activeMeta.group]}</span>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 text-primary flex items-center justify-center shrink-0">
                <ActiveIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                  {activeMeta.label}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">{activeMeta.description}</p>
              </div>
            </div>
            <Separator className="mt-6" />
          </header>

          {/* Mobile section description */}
          <header className="mb-5 lg:hidden">
            <p className="text-sm text-muted-foreground">{activeMeta.description}</p>
            <Separator className="mt-4" />
          </header>

          {children}
        </div>
      </main>
    </div>
  );
}

/** Hook to sync active section with URL `?section=` */
export function useAdminSection(defaultSection: AdminSectionId = "users") {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("section") as AdminSectionId | null;
  const valid = ADMIN_SECTIONS.some((s) => s.id === raw);
  const active: AdminSectionId = valid ? (raw as AdminSectionId) : defaultSection;

  const setActive = (id: AdminSectionId) => {
    const next = new URLSearchParams(searchParams);
    if (id === defaultSection) next.delete("section");
    else next.set("section", id);
    setSearchParams(next, { replace: false });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active]);

  return [active, setActive] as const;
}
